import { sort } from "fast-sort";
import { z } from "zod";

export const phaseDraftSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().trim().min(1),
    description: z.string(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    start: z.number().int(),
    end: z.number().int(),
  })
  .refine((phase) => phase.end > phase.start, { path: ["end"] });

export type PhaseDraft = z.infer<typeof phaseDraftSchema>;

export interface PhaseRange {
  start: number;
  end: number;
}

export const DEFAULT_PHASE_COLOR = "#64748B";
export const NEW_PHASE_WEEK_COUNT = 4;
export const MAX_PHASE_LANES = 3;

export function getPhaseRangeFrom(
  range: PhaseRange,
  start: number,
): PhaseRange {
  if (start <= range.start || start >= range.end) {
    return range;
  }

  return { start, end: range.end };
}

export function hasOverlappingPhases(phases: PhaseRange[]) {
  const sortedPhases = phases.slice().sort((a, b) => {
    return a.start - b.start;
  });
  return sortedPhases.some((phase, index) => {
    const previousPhase = sortedPhases[index - 1];
    return previousPhase ? phase.start < previousPhase.end : false;
  });
}

export function getEmptyPhaseRanges(
  phases: PhaseRange[],
  start: number,
  end: number,
) {
  const ranges: PhaseRange[] = [];
  let rangeStart = start;

  phases
    .slice()
    .sort((a, b) => {
      return a.start - b.start;
    })
    .forEach((phase) => {
      if (rangeStart < phase.start) {
        ranges.push({ start: rangeStart, end: Math.min(phase.start, end) });
      }

      rangeStart = Math.max(rangeStart, phase.end);
    });

  if (rangeStart < end) {
    ranges.push({ start: rangeStart, end });
  }

  return ranges.filter((range) => {
    return range.start < range.end;
  });
}

export function assignPhaseLanes<T extends PhaseRange>(phases: T[]) {
  const laneEnds: number[] = [];

  return sort(phases)
    .by([
      {
        asc: (phase) => {
          return phase.start;
        },
      },
      {
        desc: (phase) => {
          return phase.end;
        },
      },
    ])
    .map((phase) => {
      const freeLane = laneEnds.findIndex((laneEnd) => {
        return laneEnd <= phase.start;
      });
      const lane = freeLane === -1 ? laneEnds.length : freeLane;
      laneEnds[lane] = phase.end;

      return { ...phase, lane };
    });
}
